import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { MaintCardShell } from './MaintCardShell';
import { ConfirmApplyDialog } from './ConfirmApplyDialog';

export default function RepairLeadsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/repair-converted-leads/scan');
      setData(res.data);
      const all = {};
      res.data.leads.forEach((l) => { all[l.booking_id] = true; });
      setSelected(all);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setLoading(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const apply = async () => {
    setApplying(true);
    try {
      const bookingIds = data.leads.filter((l) => selected[l.booking_id]).map((l) => l.booking_id);
      // One booking_id per request (sequential): a bulk parcel lookup for
      // several leads at once risks a 60s gateway timeout (each geocode +
      // parcel search takes ~10-15s), reported by testing.
      let repaired = 0;
      for (const id of bookingIds) {
        const res = await api.post('/admin/maintenance/repair-converted-leads/apply', { booking_ids: [id] });
        repaired += res.data.total_repaired;
      }
      toast.success(`${repaired} lead(s) repaired`);
      setConfirmOpen(false);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <MaintCardShell
      title="Converted leads left incomplete"
      description="Leads converted before the TEMA F fix that ended up with an estimate but no project and/or no parcel (Ed Días / BK-2026-001). Only completes what's missing — never creates a new estimate."
      testId="repair-leads"
      count={data ? data.total_affected : null}
      loading={loading}
      onScan={scan}
    >
      {data && data.leads.length > 0 && (
        <div className="space-y-3">
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.leads.map((l) => (
              <div key={l.booking_id} className="border border-gray-100 rounded-lg p-3 text-xs" data-testid={`repair-leads-row-${l.booking_id}`}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox data-testid={`repair-leads-row-${l.booking_id}-checkbox`} checked={!!selected[l.booking_id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [l.booking_id]: !!v }))} />
                  <div>
                    <p className="font-semibold text-gray-800">{l.booking_number} · {l.customer_name || '(no name)'} · {l.estimate_number}</p>
                    <p className="text-gray-500">{l.address}</p>
                    <p className="text-amber-700">{l.missing_project && 'Missing project'}{l.missing_project && l.missing_parcel && ' · '}{l.missing_parcel && 'Missing parcel'}</p>
                  </div>
                </label>
              </div>
            ))}
          </div>
          <Button data-testid="repair-leads-apply-btn" size="sm" disabled={selectedCount === 0} onClick={() => setConfirmOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
            Apply selected ({selectedCount})
          </Button>
        </div>
      )}
      {data && data.leads.length === 0 && <p className="text-xs text-emerald-600">No incomplete converted leads found.</p>}

      <ConfirmApplyDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={apply}
        confirming={applying}
        title="Repair converted leads"
        summary={`This will create the missing project and/or search for the parcel on ${selectedCount} selected lead(s). Never creates a duplicate estimate.`}
      />
    </MaintCardShell>
  );
}
